import React, { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Check, ChevronDown, ShoppingCart, Star } from "lucide-react";
import { ProductDetailData } from "../ProductDetailTypes";
import { useCart } from "../../../../context/CartContext";
import { ProductType } from "../../../../Redux/custmoer_cart_service/types";
import "./SimilarProductsComparison.css";

const PRODUCT_BASE_URL = `${process.env.REACT_APP_API_BASE_URL || ""}${process.env.REACT_APP_ECOMMERCE_PERFIX || ""}`;

export type SimilarProductItem = {
    id: string | number;
    title: string;
    image: string;
    price: number;
    mrp: number;
    rating: number;
    reviews: string;
    brand: string;
    category: string;
    condition: string;
    returnPolicy: string;
    warranty: string;
    shipping: string;
    sellerId?: string;
    sellerSkuId?: string;
    sellerName?: string;
    attributes: Record<string, any>;
};

type SimilarProductsComparisonProps = {
    product: ProductDetailData;
};

const formatPrice = (value: number) =>
    new Intl.NumberFormat("en-IN").format(value);

const getComparisonTitle = (category?: string) => {
    const value = (category || "").toLowerCase();

    if (value.includes("women")) return "women's styles";
    if (value.includes("men")) return "men's styles";
    if (value.includes("fashion") || value.includes("cloth")) return "styles";
    if (value.includes("electronic") || value.includes("mobile") || value.includes("phone"))
        return "electronics";
    if (value.includes("appliance") || value.includes("home")) return "appliances";
    if (
        value.includes("grocery") ||
        value.includes("grocer") ||
        value.includes("fresh")
    ) {
        return "products";
    }

    return "products";
};

const getFirstAttribute = (
    attributes: Record<string, any>,
    keys: string[],
    fallback = "-",
) => {
    for (const key of keys) {
        const value = attributes?.[key];
        if (value !== undefined && value !== null && String(value).trim() !== "") {
            return String(value).trim();
        }
    }
    return fallback;
};

const mapToSimilarItem = (item: any): SimilarProductItem => {
    const featuredOffer = item.featuredOffer || {};
    const price = Number(
        featuredOffer.finalPrice ??
        featuredOffer.displayPrice ??
        featuredOffer.sellerPrice ??
        featuredOffer.unitPrice ??
        item.price ??
        0
    );
    const mrp = Number(featuredOffer.mrp ?? item.mrp ?? price);
    const rating = Number(item.reviews?.averageRating ?? item.rating ?? 0);
    const reviews = item.reviews?.totalReviews
        ? String(item.reviews.totalReviews)
        : item.reviews
        ? String(item.reviews)
        : rating > 0
        ? "Verified"
        : "No reviews";

    const rawBrand =
        item.brand?.name ||
        item.brandName ||
        (typeof item.brand === "string" ? item.brand : undefined);
    const brand = rawBrand && rawBrand.trim() ? rawBrand.trim() : "-";

    const catHierarchy = item.categoryHierarchy || {};
    const category =
        catHierarchy.childCategory ||
        catHierarchy.subCategory ||
        catHierarchy.category ||
        catHierarchy.superCategory ||
        "-";

    const condition = item.condition ? String(item.condition) : "New";

    const returnPolicy =
        item.returnPolicy ||
        featuredOffer.returnPolicy ||
        item.attributes?.["Return Policy"] ||
        item.attributes?.returnPolicy ||
        "7-day return";

    const warranty =
        item.warranty ||
        featuredOffer.warranty ||
        item.attributes?.Warranty ||
        item.attributes?.warranty ||
        "-";

    const shipping =
        featuredOffer.freeDelivery || item.freeDelivery
            ? "Free Delivery"
            : featuredOffer.fastDelivery || item.fastDelivery
            ? "Fast Delivery"
            : "Standard Delivery";

    return {
        id: item._id || item.id || item.ssin,
        title: item.name || item.title || "-",
        image: item.mainImage || item.image || (item.images && item.images[0]) || "",
        price,
        mrp,
        rating,
        reviews,
        brand,
        category,
        condition,
        returnPolicy,
        warranty,
        shipping,
        sellerId: featuredOffer.sellerId || item.sellerId,
        sellerSkuId: featuredOffer.sellerSkuId || item.sellerSkuId,
        sellerName: featuredOffer.sellerName || item.sellerName,
        attributes: item.attributes || {},
    };
};

const SimilarProductsComparison = ({
    product,
}: SimilarProductsComparisonProps) => {
    const navigate = useNavigate();
    const { addToCart, setCartOpen } = useCart();

    const [similarItems, setSimilarItems] = useState<SimilarProductItem[]>(() => {
        if (Array.isArray(product.similarProducts) && product.similarProducts.length > 0) {
            return product.similarProducts
                .filter(
                    (p: any) =>
                        String(p._id || p.id || p.ssin) !== String(product.id)
                )
                .map(mapToSimilarItem);
        }
        return [];
    });

    const [expandedProducts, setExpandedProducts] = useState<string[]>([]);
    const [addedProductIds, setAddedProductIds] = useState<string[]>([]);

    const toggleProductDetails = (id: string | number) => {
        const strId = String(id);
        setExpandedProducts((current) =>
            current.includes(strId)
                ? current.filter((itemId) => itemId !== strId)
                : [...current, strId]
        );
    };

    const [loading, setLoading] = useState<boolean>(similarItems.length === 0);
    const [addingId, setAddingId] = useState<string | null>(null);

    useEffect(() => {
        let isMounted = true;

        if (Array.isArray(product.similarProducts) && product.similarProducts.length > 0) {
            const mapped = product.similarProducts
                .filter(
                    (p: any) =>
                        String(p._id || p.id || p.ssin) !== String(product.id)
                )
                .map(mapToSimilarItem);
            setSimilarItems(mapped);
            setLoading(false);
            return;
        }

        if (product.id) {
            setLoading(true);
            axios
                .get(
                    `${PRODUCT_BASE_URL}/e-commerce/catalog/products/${product.id}/similar?limit=6`
                )
                .then((res) => {
                    if (!isMounted) return;
                    const data = res.data?.data;
                    if (Array.isArray(data)) {
                        const mapped = data
                            .filter(
                                (p: any) =>
                                    String(p._id || p.id || p.ssin) !==
                                    String(product.id)
                            )
                            .map(mapToSimilarItem);
                        setSimilarItems(mapped);
                    } else {
                        setSimilarItems([]);
                    }
                })
                .catch((err) => {
                    console.error("Failed to fetch similar products:", err);
                    if (isMounted) setSimilarItems([]);
                })
                .finally(() => {
                    if (isMounted) setLoading(false);
                });
        } else {
            setSimilarItems([]);
            setLoading(false);
        }

        return () => {
            isMounted = false;
        };
    }, [product.id, product.similarProducts]);

    const handleAddToCart = async (
        item: SimilarProductItem,
        e?: React.MouseEvent
    ) => {
        e?.stopPropagation();
        const strId = String(item.id);
        if (addedProductIds.includes(strId)) return;
        setAddingId(strId);

        try {
            await addToCart({
                productId: strId,
                productName: item.title,
                productImage: item.image,
                productType: ProductType.PHYSICAL,
                quantity: 1,
                unitPrice: item.price,
                price: item.price,
                mrp: item.mrp || item.price,
                sellerId: item.sellerId,
                sellerSkuId: item.sellerSkuId,
                sellerName: item.sellerName,
                isMLMProduct: false,
                metadata: {
                    mrp: item.mrp || item.price,
                    image: item.image,
                },
            });
            setCartOpen(true);
            setAddedProductIds((curr) =>
                curr.includes(strId) ? curr : [...curr, strId]
            );
        } catch (err) {
            console.error("Failed to add similar product to cart:", err);
        } finally {
            setTimeout(() => {
                setAddingId((curr) => (curr === strId ? null : curr));
            }, 1500);
        }
    };

    // If no similar products are available, do not show this component
    if (!loading && similarItems.length === 0) {
        return null;
    }

    if (loading && similarItems.length === 0) {
        return null;
    }

    // Build dynamic comparison rows based on available similar products
    const comparisonRows: {
        label: string;
        render: (item: SimilarProductItem) => React.ReactNode;
    }[] = [
        {
            label: "Customer Reviews",
            render: (item: SimilarProductItem) => (
                <div className="comparison-rating">
                    <strong>{item.rating > 0 ? item.rating.toFixed(1) : "-"}</strong>
                    {item.rating > 0 && <Star size={13} fill="currentColor" />}
                    <span>({item.reviews})</span>
                </div>
            ),
        },
        {
            label: "Price",
            render: (item: SimilarProductItem) => (
                <div className="comparison-price-cell">
                    <strong className="comparison-price">
                        ₹{formatPrice(item.price)}
                    </strong>
                    {item.mrp > item.price && (
                        <span className="comparison-mrp">
                            ₹{formatPrice(item.mrp)}
                        </span>
                    )}
                </div>
            ),
        },
    ];

    if (similarItems.some((it) => it.brand && it.brand !== "-")) {
        comparisonRows.push({
            label: "Brand",
            render: (item: SimilarProductItem) => item.brand || "-",
        });
    }

    if (similarItems.some((it) => it.category && it.category !== "-")) {
        comparisonRows.push({
            label: "Category",
            render: (item: SimilarProductItem) => item.category || "-",
        });
    }

    const hasMaterial = similarItems.some(
        (it) =>
            it.attributes?.Fabric ||
            it.attributes?.Material ||
            it.attributes?.fabric ||
            it.attributes?.material
    );
    if (hasMaterial) {
        comparisonRows.push({
            label: "Material",
            render: (item: SimilarProductItem) =>
                item.attributes?.Fabric ||
                item.attributes?.Material ||
                item.attributes?.fabric ||
                item.attributes?.material ||
                "-",
        });
    }

    const hasFit = similarItems.some(
        (it) =>
            it.attributes?.Fit ||
            it.attributes?.fitType ||
            it.attributes?.fit
    );
    if (hasFit) {
        comparisonRows.push({
            label: "Fit / Type",
            render: (item: SimilarProductItem) =>
                item.attributes?.Fit ||
                item.attributes?.fitType ||
                item.attributes?.fit ||
                "-",
        });
    }

    const hasColor = similarItems.some(
        (it) =>
            it.attributes?.Color ||
            it.attributes?.color ||
            it.attributes?.Colour
    );
    if (hasColor) {
        comparisonRows.push({
            label: "Color",
            render: (item: SimilarProductItem) =>
                item.attributes?.Color ||
                item.attributes?.color ||
                item.attributes?.Colour ||
                "-",
        });
    }

    const hasWarranty = similarItems.some(
        (it) => it.warranty && it.warranty !== "-"
    );
    if (hasWarranty) {
        comparisonRows.push({
            label: "Warranty",
            render: (item: SimilarProductItem) => item.warranty || "-",
        });
    }

    comparisonRows.push({
        label: "Shipping",
        render: (item: SimilarProductItem) => item.shipping || "Standard Delivery",
    });

    comparisonRows.push({
        label: "Return Policy",
        render: (item: SimilarProductItem) => item.returnPolicy || "7-day return",
    });

    return (
        <section className="similar-comparison-section">
            <div className="similar-comparison-inner">
                <div className="similar-comparison-heading">
                    <span>EXPLORE MORE</span>
                    <h2>
                        Compare similar {getComparisonTitle(product.category)}
                    </h2>
                    <p>
                        Compare key details and choose the option that suits your
                        needs best.
                    </p>
                </div>

                <div className="similar-comparison-desktop">
                    <div className="comparison-scroll">
                        <div
                            className="comparison-grid"
                            style={{
                                gridTemplateColumns: `185px repeat(${similarItems.length}, 220px)`,
                                minWidth: `${185 + similarItems.length * 220}px`,
                            }}
                        >
                            <div className="comparison-product-label">
                                <span>Related products</span>
                                <strong>Quick comparison</strong>
                            </div>

                            {similarItems.map((item) => (
                                <article
                                    className="comparison-product-card"
                                    key={`similar-${item.id}`}
                                    onClick={() => navigate(`/DetailPage/${item.id}`)}
                                    style={{ cursor: "pointer" }}
                                >
                                    <div className="comparison-product-image">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).src =
                                                    "/assets/images/logos/salarlogo.png";
                                            }}
                                        />
                                    </div>

                                    <h3 title={item.title}>{item.title}</h3>

                                    <div className="comparison-card-rating">
                                        <strong>
                                            {item.rating > 0
                                                ? item.rating.toFixed(1)
                                                : "-"}
                                        </strong>
                                        {item.rating > 0 && (
                                            <Star size={13} fill="currentColor" />
                                        )}
                                        <span>({item.reviews})</span>
                                    </div>

                                    <div className="comparison-card-price">
                                        ₹{formatPrice(item.price)}
                                    </div>

                                    <button
                                        type="button"
                                        className="comparison-add-cart-btn"
                                        onClick={(e) => handleAddToCart(item, e)}
                                        disabled={addingId === String(item.id)}
                                    >
                                        {addingId === String(item.id) ? (
                                            <>
                                                <Check size={14} />
                                                Added
                                            </>
                                        ) : (
                                            <>
                                                <ShoppingCart size={14} />
                                                Add to Cart
                                            </>
                                        )}
                                    </button>
                                </article>
                            ))}

                            {comparisonRows.map((row) => (
                                <Fragment key={row.label}>
                                    <div className="comparison-row-label">
                                        {row.label}
                                    </div>

                                    {similarItems.map((item) => (
                                        <div
                                            className="comparison-value"
                                            key={`${row.label}-${item.id}`}
                                        >
                                            {row.render(item)}
                                        </div>
                                    ))}
                                </Fragment>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="similar-comparison-mobile">
                    <div className="similar-mobile-topline">
                        <strong>{similarItems.length} recommendations</strong>
                        <span>Swipe to compare</span>
                    </div>

                    <div className="similar-mobile-scroll">
                        {similarItems.map((item, index) => {
                            const expanded = expandedProducts.includes(String(item.id));
                            const isAdded = addedProductIds.includes(String(item.id));
                            const fabric = getFirstAttribute(item.attributes, [
                                "Fabric",
                                "Material",
                                "fabric",
                                "material",
                            ]);
                            const fit = getFirstAttribute(item.attributes, [
                                "Fit",
                                "fitType",
                                "fit",
                            ]);
                            const occasion = getFirstAttribute(
                                item.attributes,
                                ["Occasion", "occasion"],
                                item.category || "-"
                            );
                            const washCare = getFirstAttribute(item.attributes, [
                                "Wash Care",
                                "washCare",
                                "Care",
                                "care",
                            ]);
                            const sleeve = getFirstAttribute(item.attributes, [
                                "Sleeve",
                                "sleeve",
                                "Style",
                                "style",
                                "Format",
                                "format",
                            ]);

                            return (
                                <article
                                    className="similar-mobile-card"
                                    key={`mobile-similar-${item.id}`}
                                >
                                    <div className="similar-mobile-card-top">
                                        <span className="similar-mobile-rank">
                                            #{index + 1}
                                        </span>
                                        <span className="similar-mobile-rating">
                                            <Star size={12} fill="currentColor" />
                                            {item.rating > 0
                                                ? item.rating.toFixed(1)
                                                : "-"}
                                            <small>({item.reviews})</small>
                                        </span>
                                    </div>

                                    <div className="similar-mobile-image">
                                        <img
                                            src={item.image}
                                            alt={item.title}
                                            onError={(e) => {
                                                (e.currentTarget as HTMLImageElement).src =
                                                    "/assets/images/logos/salarlogo.png";
                                            }}
                                        />
                                    </div>

                                    <h3 title={item.title}>{item.title}</h3>

                                    <div className="similar-mobile-price">
                                        ₹{formatPrice(item.price)}
                                    </div>

                                    <div className="similar-mobile-specs">
                                        <div>
                                            <span>Material</span>
                                            <strong>{fabric}</strong>
                                        </div>
                                        <div>
                                            <span>Fit / Type</span>
                                            <strong>{fit}</strong>
                                        </div>
                                        <div>
                                            <span>Best For</span>
                                            <strong>{occasion}</strong>
                                        </div>
                                        <div>
                                            <span>Returns</span>
                                            <strong>
                                                {item.returnPolicy || "7-day return"}
                                            </strong>
                                        </div>

                                        {expanded && (
                                            <>
                                                <div>
                                                    <span>Care</span>
                                                    <strong>{washCare}</strong>
                                                </div>
                                                <div>
                                                    <span>Style / Format</span>
                                                    <strong>{sleeve}</strong>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    <button
                                        type="button"
                                        className={`similar-mobile-more ${
                                            expanded ? "open" : ""
                                        }`}
                                        onClick={() => toggleProductDetails(item.id)}
                                        aria-expanded={expanded}
                                    >
                                        <span>
                                            {expanded ? "Show less" : "View details"}
                                        </span>
                                        <ChevronDown size={15} strokeWidth={2} />
                                    </button>

                                    <button
                                        type="button"
                                        className={`similar-mobile-cart ${
                                            isAdded ? "added" : ""
                                        }`}
                                        onClick={() => handleAddToCart(item)}
                                        aria-label={
                                            isAdded
                                                ? `${item.title} added to cart`
                                                : `Add ${item.title} to cart`
                                        }
                                    >
                                        {isAdded ? (
                                            <>
                                                <Check size={16} strokeWidth={2.4} />
                                                Added to Cart
                                            </>
                                        ) : (
                                            <>
                                                <ShoppingCart size={16} strokeWidth={2.1} />
                                                Add to Cart
                                            </>
                                        )}
                                    </button>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default SimilarProductsComparison;
